import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import axios from 'axios'
import { OCCUPANCY } from '@/helpers/feature-constants.js'

Vue.use(Router)

const router = new Router({
  mode: process.env.VUE_APP_PREVIEW ? 'hash' : 'history',
  base: process.env.VUE_APP_PUBLIC_PATH,
  routes: [
    {
      path: '/',
      component: () => import('@/views/Index'),
      children: [
        {
          name: 'Live Events',
          path: '',
          component: () => import('@/views/landing/LandingPage'),
        },
        {
          name: 'Organizations',
          path: '/organizations',
          component: () => import('@/views/orgs/OrganizationsList'),
          meta: {
            permissionRequired: () => store.getters['Permissions/isInternal'],
          },
        },
        {
          name: 'Organization Details',
          path: '/organizations/:orgId',
          component: () => import('@/views/orgs/OrganizationDetails'),
          meta: {
            permissionRequired: (to) => store.getters['Permissions/canAdminOrg'](to.params.orgId),
          },
        },
        {
          name: 'Admin User',
          path: '/organizations/:orgId/users/:id',
          component: () => import('@/views/orgs/AdminUser'),
          async beforeEnter (to, from, next) {
            try {
              const res = await axios.get(`${process.env.VUE_APP_PORTAL}/user/${to.params.id}`)
              const user = res.data.data
              if (store.getters['Permissions/canAdminUserOnOrg'](user, to.params.orgId)) next()
              else next({ name: 'Organization Details', params: { orgId: to.params.orgId } })
            } catch (e) {
              next({ name: 'Organization Details', params: { orgId: to.params.orgId } })
            }
          },
        },
        {
          name: 'Add User to Organization',
          path: '/organizations/:id/add_user',
          component: () => import('@/views/orgs/OrganizationAddUser'),
          meta: {
            permissionRequired: (to) => store.getters['Permissions/canAdminOrg'](to.params.id),
          },
        },
        {
          name: 'Manage Integrations',
          path: '/organizations/:orgId/integrations',
          component: () => import('@/views/integrations/ManageIntegrations'),
          meta: {
            permissionRequired: (to) => store.getters['Permissions/canAdminOrg'](to.params.orgId),
          },
        },
        {
          name: 'Property Mapping',
          path: '/organizations/:orgId/integrations/:partnerName',
          component: () => import('@/views/integrations/PropertyMatchingContainer'),
          meta: {
            permissionRequired: (to) => store.getters['Permissions/canAdminOrg'](to.params.orgId),
          },
        },
        {
          name: 'Add Property',
          path: '/properties/add_property',
          component: () => import('@/views/properties/AddPropertyDetails'),
          meta: {
            permissionRequired: () => store.getters['Permissions/canManageSelectedOrg'],
          },
        },
        {
          name: 'Properties',
          path: '/properties',
          component: () => import('@/views/properties/Properties'),
        },
        {
          name: 'Property Details',
          path: '/properties/:id',
          component: () => import('@/views/properties/PropertyDetails'),
          meta: {
            permissionRequired: (to) => store.getters['Permissions/canViewProperty'](to.params.id),
          },
        },
        {
          name: 'Edit Property',
          path: '/properties/:id/edit',
          component: () => import('@/views/properties/EditPropertyDetails'),
          meta: {
            permissionRequired: (to) => store.getters['Permissions/canEditProperty'](to.params.id),
          },
        },
        {
          name: 'Edit Sensor',
          path: 'properties/zone/:id/edit',
          component: () => import('@/components/activityZones/ZoneEdit'),
          props: true,
          beforeEnter (to, from, next) {
            if (store.getters['Permissions/canEditProperty'](from.params.id)) next()
            else next('/')
          },
        },
        {
          name: 'Users',
          path: '/users',
          component: () => import('@/views/users/UsersList'),
          meta: {
            permissionRequired: () => store.getters['Permissions/isInternal'],
          },
        },
        {
          name: 'User Details',
          path: '/users/:id',
          component: () => import('@/views/users/UserDetails'),
          meta: {
            permissionRequired: () => store.getters['Permissions/isInternal'],
          },
        },
        {
          name: 'Account Settings',
          path: '/account',
          component: () => import('@/views/users/AccountSettings'),
        },
        {
          name: 'Events History',
          path: '/events_history',
          component: () => import('@/views/events/EventsList'),
          props: { history: true },
        },
        {
          name: 'Reservations',
          path: '/reservations',
          component: () => import('@/views/reservations/Reservations'),
          meta: {
            permissionRequired: () => {
              return store.getters['Organizations/selectedOrg'] &&
              (
                store.getters['Permissions/isNightAgent'] ||
                store.getters['Permissions/isInternal']
              )
            },
          },
        },
        {
          name: 'Billing',
          path: '/billing',
          component: () => import('@/views/billing/Billing'),
          meta: {
            permissionsRequired: () => {
              return store.getters['Permissions/canUpdateSelectedOrgBilling']
            },
          },
        },
        {
          name: 'Billing Update',
          path: '/billing/edit',
          component: () => import('@/views/billing/BillingUpdate'),
          meta: {
            permissionsRequired: () => {
              return store.getters['Permissions/canUpdateSelectedOrgBilling']
            },
          },
        },
      ],
    },
    {
      path: '*',
      component: () => import('@/views/Index'),
      children: [
        {
          name: '404 Error',
          path: '',
          component: () => import('@/views/pages/Error'),
        },
      ],
    },
  ],
})

const checkRoute = route => {
  const approvedRoutes = ['Events']
  return approvedRoutes.includes(route)
}

const parentPath = route => {
  const parentPath = route.path.split('/').slice(0, -1).join('/')
  return parentPath === '' ? '/' : parentPath
}

router.beforeEach(async (to, from, next) => {
  if (router.app.$root.$confirm) router.app.$root.$confirm.$close()
  if (store.state.fromDashboard && !checkRoute(to.name)) {
      window.location = `${process.env.VUE_APP_DASHBOARD}${to.path}`
  }
  if (!store.state.userToken) {
    await store.dispatch('setToken')
      .then(() => store.dispatch('Users/fetchCurrentUser'))
      .then(() => store.dispatch('Permissions/fetchUserPermissions'))
  }
  const orgSelected = store.getters['Permissions/currentSelectedOrg'].id || to.params.orgId
  if (orgSelected && !store.state.Organizations.orgFeaturesFetched) {
    await store.dispatch('Organizations/fetchOrgFlags', { orgId: orgSelected })
    await store.dispatch('Organizations/fetchOrgFeatureEligibility', { orgId: orgSelected, feature: OCCUPANCY })
  }
  if (to.meta.permissionRequired) {
    if (to.meta.permissionRequired(to)) next()
    else next(parentPath(to))
  } else next()
})

export default router
